import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    CircularProgress,
    Alert,
    Grid,
    Tooltip,
    TablePagination
} from '@mui/material';
import {
    Refresh,
    Edit,
    Close,
    FilterList,
    WhatsApp,
    Computer,
    DevicesOther
} from '@mui/icons-material';
import { summariesAPI } from '../../services/api';
import './CustomerSummary.css';

const CustomerSummary = () => {
    const [summaries, setSummaries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [stats, setStats] = useState(null);
    
    // Pagination
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [pagination, setPagination] = useState(null);
    
    // Filters
    const [filters, setFilters] = useState({
        priority: 'All',
        status: 'All',
        chat_type: 'All',
        search: ''
    });
    
    // Edit dialog
    const [editDialog, setEditDialog] = useState(false);
    const [selectedSummary, setSelectedSummary] = useState(null);
    const [editForm, setEditForm] = useState({
        priority: '',
        status: '',
        action_notes: ''
    });

    useEffect(() => {
        setPage(0); // Reset to first page when filters change
        fetchSummaries();
        fetchStats();
    }, [filters]);

    useEffect(() => {
        fetchSummaries();
    }, [page, rowsPerPage]);

    const fetchSummaries = async () => {
        try {
            setLoading(true);
            setError('');
            console.log('🔍 Fetching summaries with filters:', filters, 'Page:', page + 1, 'Limit:', rowsPerPage);
            const response = await summariesAPI.getAll({
                ...filters,
                page: page + 1, // API uses 1-based pagination
                limit: rowsPerPage
            });
            console.log('✅ Summaries response:', response.data);
            setSummaries(response.data.data);
            setPagination(response.data.pagination);
            console.log('📊 Summaries set to state:', response.data.data);
            console.log('📄 Pagination:', response.data.pagination);
        } catch (err) {
            console.error('❌ Error fetching summaries:', err);
            console.error('Error details:', err.response?.data);
            setError('Failed to load customer summaries');
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            console.log('📈 Fetching stats...');
            const response = await summariesAPI.getStats();
            console.log('✅ Stats response:', response.data);
            setStats(response.data.data);
        } catch (err) {
            console.error('❌ Failed to load stats:', err);
            console.error('Stats error details:', err.response?.data);
        }
    };

    const handleGenerateSummaries = async () => {
        if (!window.confirm('This will analyze all customer conversations and generate summaries. This may take a few minutes. Continue?')) {
            return;
        }
        
        try {
            setGenerating(true);
            setError('');
            setSuccess('');
            const response = await summariesAPI.generate();
            setSuccess(response.data.message);
            fetchSummaries();
            fetchStats();
        } catch (err) {
            setError('Failed to generate summaries. Please try again.');
            console.error(err);
        } finally {
            setGenerating(false);
        }
    };

    const handleEditClick = (summary) => {
        setSelectedSummary(summary);
        setEditForm({
            priority: summary.priority,
            status: summary.status,
            action_notes: summary.action_notes || ''
        });
        setEditDialog(true);
    };

    const handleEditSave = async () => {
        try {
            console.log('💾 Saving summary update:', {
                summaryId: selectedSummary.id,
                editForm: editForm
            });
            const response = await summariesAPI.update(selectedSummary.id, editForm);
            console.log('✅ Update response:', response.data);
            setSuccess('Summary updated successfully');
            setEditDialog(false);
            fetchSummaries();
            fetchStats();
        } catch (err) {
            console.error('❌ Failed to update summary:', err);
            console.error('Error details:', err.response?.data);
            setError('Failed to update summary');
        }
    };

    const getPriorityColor = (priority) => {
        switch (priority) {
            case 'High': return 'error';
            case 'Medium': return 'warning';
            case 'Low': return 'success';
            default: return 'default';
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Open': return 'error';
            case 'In Progress': return 'warning';
            case 'Closed': return 'success';
            case 'Deal': return 'info';
            default: return 'default';
        }
    };

    const getChatTypeIcon = (chatType) => {
        switch (chatType) {
            case 'WhatsApp': return <WhatsApp sx={{ fontSize: 16 }} />;
            case 'Web': return <Computer sx={{ fontSize: 16 }} />;
            case 'Both': return <DevicesOther sx={{ fontSize: 16 }} />;
            default: return null;
        }
    };

    return (
        <Box className="customer-summary-container" sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                    Customer Summary Report
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleGenerateSummaries}
                    disabled={generating}
                    startIcon={generating ? <CircularProgress size={20} /> : <Refresh />}
                >
                    {generating ? 'Generating...' : 'Generate Summaries'}
                </Button>
            </Box>

            {/* Messages */}
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

            {/* Statistics Cards */}
            {stats && (
                <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'space-between' }}>
                    <Card sx={{ flex: '1 1 calc(14.28% - 16px)', minWidth: '150px' }}>
                        <CardContent>
                            <Typography variant="h6" color="text.secondary">Total Customers</Typography>
                            <Typography variant="h3" sx={{ fontWeight: 600 }}>{stats.total}</Typography>
                        </CardContent>
                    </Card>
                    <Card sx={{ flex: '1 1 calc(14.28% - 16px)', minWidth: '150px' }}>
                        <CardContent>
                            <Typography variant="h6" color="error">High Priority</Typography>
                            <Typography variant="h3" sx={{ fontWeight: 600, color: 'error.main' }}>{stats.high_priority}</Typography>
                        </CardContent>
                    </Card>
                    <Card sx={{ flex: '1 1 calc(14.28% - 16px)', minWidth: '150px' }}>
                        <CardContent>
                            <Typography variant="h6" color="warning.main">Medium Priority</Typography>
                            <Typography variant="h3" sx={{ fontWeight: 600, color: 'warning.main' }}>{stats.medium_priority}</Typography>
                        </CardContent>
                    </Card>
                    <Card sx={{ flex: '1 1 calc(14.28% - 16px)', minWidth: '150px' }}>
                        <CardContent>
                            <Typography variant="h6" color="text.secondary">Open Cases</Typography>
                            <Typography variant="h3" sx={{ fontWeight: 600, color: 'error.main' }}>{stats.open || 0}</Typography>
                        </CardContent>
                    </Card>
                    <Card sx={{ flex: '1 1 calc(14.28% - 16px)', minWidth: '150px' }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ color: '#ff9800' }}>In Progress</Typography>
                            <Typography variant="h3" sx={{ fontWeight: 600, color: '#ff9800' }}>{stats.in_progress || 0}</Typography>
                        </CardContent>
                    </Card>
                    <Card sx={{ flex: '1 1 calc(14.28% - 16px)', minWidth: '150px' }}>
                        <CardContent>
                            <Typography variant="h6" color="success.main">Closed</Typography>
                            <Typography variant="h3" sx={{ fontWeight: 600, color: 'success.main' }}>{stats.closed || 0}</Typography>
                        </CardContent>
                    </Card>
                    <Card sx={{ flex: '1 1 calc(14.28% - 16px)', minWidth: '150px' }}>
                        <CardContent>
                            <Typography variant="h6" sx={{ color: '#2196f3' }}>Deals</Typography>
                            <Typography variant="h3" sx={{ fontWeight: 600, color: '#2196f3' }}>{stats.deal || 0}</Typography>
                        </CardContent>
                    </Card>
                </Box>
            )}

            {/* Filters */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <FilterList />
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Priority</InputLabel>
                            <Select
                                value={filters.priority}
                                label="Priority"
                                onChange={(e) => setFilters({ ...filters, priority: e.target.value })}
                            >
                                <MenuItem value="All">All</MenuItem>
                                <MenuItem value="High">High</MenuItem>
                                <MenuItem value="Medium">Medium</MenuItem>
                                <MenuItem value="Low">Low</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={filters.status}
                                label="Status"
                                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                            >
                                <MenuItem value="All">All</MenuItem>
                                <MenuItem value="Open">Open</MenuItem>
                                <MenuItem value="In Progress">In Progress</MenuItem>
                                <MenuItem value="Closed">Closed</MenuItem>
                                <MenuItem value="Deal">Deal</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Chat Type</InputLabel>
                            <Select
                                value={filters.chat_type}
                                label="Chat Type"
                                onChange={(e) => setFilters({ ...filters, chat_type: e.target.value })}
                            >
                                <MenuItem value="All">All</MenuItem>
                                <MenuItem value="Web">Web</MenuItem>
                                <MenuItem value="WhatsApp">WhatsApp</MenuItem>
                                <MenuItem value="Both">Both</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            size="small"
                            placeholder="Search customer..."
                            value={filters.search}
                            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                            sx={{ flexGrow: 1 }}
                        />
                    </Box>
                </CardContent>
            </Card>

            {/* Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#1976d2' }}>
                            <TableCell sx={{ color: '#000 !important', fontWeight: 600 }}>Customer</TableCell>
                            <TableCell sx={{ color: '#000 !important', fontWeight: 600 }}>Phone</TableCell>
                            <TableCell sx={{ color: '#000 !important', fontWeight: 600 }}>Summary</TableCell>
                            <TableCell sx={{ color: '#000 !important', fontWeight: 600 }}>Chat Type</TableCell>
                            <TableCell sx={{ color: '#000 !important', fontWeight: 600 }}>Messages</TableCell>
                            <TableCell sx={{ color: '#000 !important', fontWeight: 600 }}>Priority</TableCell>
                            <TableCell sx={{ color: '#000 !important', fontWeight: 600 }}>Status</TableCell>
                            <TableCell sx={{ color: '#000 !important', fontWeight: 600 }}>Last Chat</TableCell>
                            <TableCell sx={{ color: '#000 !important', fontWeight: 600 }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={9} align="center">
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : summaries.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={9} align="center">
                                    <Typography variant="body1" color="text.secondary">
                                        No summaries found. Click "Generate Summaries" to create them.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            summaries.map((summary) => (
                                <TableRow key={summary.id} hover>
                                    <TableCell>{summary.customer_name || 'Unknown'}</TableCell>
                                    <TableCell>{summary.customer_phone}</TableCell>
                                    <TableCell sx={{ maxWidth: 300 }}>{summary.summary}</TableCell>
                                    <TableCell>
                                        <Chip
                                            icon={getChatTypeIcon(summary.chat_type)}
                                            label={summary.chat_type}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>{summary.total_messages}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={summary.priority}
                                            color={getPriorityColor(summary.priority)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={summary.status}
                                            color={getStatusColor(summary.status)}
                                            size="small"
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        {new Date(summary.last_chat_date).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title="Edit">
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => handleEditClick(summary)}
                                            >
                                                <Edit />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Edit Dialog */}
            <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    Edit Customer Summary
                    <IconButton
                        onClick={() => setEditDialog(false)}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel>Priority</InputLabel>
                            <Select
                                value={editForm.priority}
                                label="Priority"
                                onChange={(e) => setEditForm({ ...editForm, priority: e.target.value })}
                            >
                                <MenuItem value="High">High</MenuItem>
                                <MenuItem value="Medium">Medium</MenuItem>
                                <MenuItem value="Low">Low</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                                value={editForm.status}
                                label="Status"
                                onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                            >
                                <MenuItem value="Open">Open</MenuItem>
                                <MenuItem value="In Progress">In Progress</MenuItem>
                                <MenuItem value="Closed">Closed</MenuItem>
                                <MenuItem value="Deal">Deal</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            label="Action Notes"
                            multiline
                            rows={4}
                            value={editForm.action_notes}
                            onChange={(e) => setEditForm({ ...editForm, action_notes: e.target.value })}
                            placeholder="Add notes about actions taken..."
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialog(false)}>Cancel</Button>
                    <Button onClick={handleEditSave} variant="contained" color="primary">
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Pagination */}
            {pagination && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <TablePagination
                        component="div"
                        count={pagination.totalRecords || 0}
                        page={page}
                        onPageChange={(event, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(event) => {
                            setRowsPerPage(parseInt(event.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[10, 20, 50, 100]}
                        labelRowsPerPage="Records per page:"
                    />
                </Box>
            )}
        </Box>
    );
};

export default CustomerSummary;
